import { useQuery } from "@tanstack/react-query";

import {
  getDefaultDashboardMediaBucket,
  signDashboardMedia,
} from "../providers/aws/client";
import { EXPERIMENTS_INDEX_KEY, parseIndex, parseManifest } from "./manifest";
import type { ExperimentIndex, ExperimentManifest, MediaRef } from "./types";

const signedJsonTtlSeconds = 5 * 60;

export function experimentsIndexRef(): MediaRef {
  const bucket = getDefaultDashboardMediaBucket();
  if (!bucket) {
    throw new Error(
      "No media bucket configured for this CRM (VITE_ECHOFLICKS_MEDIA_BUCKET); experiment results cannot be loaded.",
    );
  }
  return { bucket, key: EXPERIMENTS_INDEX_KEY };
}

export async function fetchSignedJson(ref: MediaRef): Promise<unknown> {
  const { url } = await signDashboardMedia({
    bucket: ref.bucket,
    key: ref.key,
    expiresIn: signedJsonTtlSeconds,
  });
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Could not load ${ref.key} (${response.status})`);
  }
  return response.json();
}

export async function loadExperimentIndex(): Promise<ExperimentIndex> {
  return parseIndex(await fetchSignedJson(experimentsIndexRef()));
}

export async function loadExperimentManifest(
  ref: MediaRef,
): Promise<ExperimentManifest> {
  return parseManifest(await fetchSignedJson(ref));
}

export function useExperimentIndex() {
  return useQuery({
    queryKey: ["experiments", "index"],
    queryFn: loadExperimentIndex,
    staleTime: 60 * 1000,
  });
}

export function useExperimentManifest(slug: string | undefined) {
  const index = useExperimentIndex();
  const summary = index.data?.experiments.find((e) => e.slug === slug);
  const manifest = useQuery({
    queryKey: ["experiments", "manifest", slug, summary?.manifest.key],
    queryFn: () => loadExperimentManifest(summary!.manifest),
    enabled: Boolean(summary),
    staleTime: 60 * 1000,
  });
  return { index, summary, manifest };
}
